/**
 * TanStack Query hooks for font library management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fontsApi, type FontRead, type FontUpdateInput, type FontUploadInput } from '@/lib/api/fonts'

const QK = {
  all: ['fonts'] as const,
  list: (includeDeleted: boolean) => ['fonts', 'list', includeDeleted] as const,
  detail: (id: number) => ['fonts', 'detail', id] as const,
}

export function useFontsList(includeDeleted = false) {
  return useQuery<FontRead[]>({
    queryKey: QK.list(includeDeleted),
    queryFn: () => fontsApi.list(includeDeleted),
    staleTime: 30_000,
  })
}

export function useFont(id: number | null | undefined) {
  return useQuery<FontRead>({
    queryKey: QK.detail(id ?? -1),
    queryFn: () => fontsApi.get(id as number),
    enabled: id != null,
    staleTime: 30_000,
  })
}

export function useFontUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: FontUploadInput) => fontsApi.upload(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  })
}

export function useFontUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: FontUpdateInput }) =>
      fontsApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  })
}

export function useFontDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => fontsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  })
}

export function useFontRestore() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => fontsApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.all }),
  })
}
